import { useState, useEffect, useRef, useMemo } from "react";
import { useDraftPersistence } from "@/hooks/useDraftPersistence";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  FileDown,
  Mail,
  Users,
  FileText,
  Leaf,
  Heart,
  Utensils,
  Sparkles,
  GripVertical,
  Upload,
  FlaskConical,
  CheckCircle2,
  AlertTriangle,
  Pill,
  Activity,
  ChevronDown,
  ChevronUp,
  Baby,
  ClipboardList,
} from "lucide-react";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { SessionReport as SessionReportType, SessionReportItem, generateDefaultReport, isBachFlowersAccompaniment } from "@/types/sessionReport";
import { downloadSessionReportPDF, generateSessionReportPDF } from "@/utils/sessionReportPdfExport";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BachFlowersSessionForm } from "@/components/session-report/BachFlowersSessionForm";
import { BachFlowersSessionData, createDefaultBachFlowersSession } from "@/types/bachFlowersSession";
import { VitalityReportForm, VitalityReportData, createDefaultVitalityReport } from "@/components/session-report/VitalityReportForm";
import { downloadVitalityReportPDF, generateVitalityReportPDF } from "@/utils/vitalityReportPdfExport";

interface Patient {
  id: string;
  name: string;
  email: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
}

interface PatientChild {
  id: string;
  name: string;
  date_of_birth: string | null;
  gender: string | null;
  parent_id: string;
}

interface Accompaniment {
  id: string;
  title: string;
  is_active: boolean;
}

interface Consultation {
  id: string;
  patient_id: string;
  symptoms_text: string;
  created_at: string;
}

interface Recommendation {
  id: string;
  consultation_id: string;
  title: string;
  detail: string;
  type: string;
  posology?: string;
  contraindications?: string[];
  priority?: number;
}

interface SelectedRecommendation extends Recommendation {
  isSelected: boolean;
}

export default function SessionReportPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientIdFromUrl = searchParams.get("patientId");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [patients, setPatients] = useState<Patient[]>([]);
  const [patientChildren, setPatientChildren] = useState<PatientChild[]>([]);
  const [accompaniments, setAccompaniments] = useState<Accompaniment[]>([]);
  const [existingReports, setExistingReports] = useState<any[]>([]);
  
  // Analysis states
  const [patientConsultations, setPatientConsultations] = useState<Consultation[]>([]);
  const [selectedConsultationId, setSelectedConsultationId] = useState<string>("");
  const [consultationRecommendations, setConsultationRecommendations] = useState<SelectedRecommendation[]>([]);
  const [importedRecommendations, setImportedRecommendations] = useState<SessionReportItem[]>([]);
  const [isRecommendationsOpen, setIsRecommendationsOpen] = useState(true);
  
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const [selectedAccompanimentId, setSelectedAccompanimentId] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedChild, setSelectedChild] = useState<PatientChild | null>(null);
  
  // Bach Flowers specific data
  const [bachFlowersData, setBachFlowersData] = useState<BachFlowersSessionData>(createDefaultBachFlowersSession());
  const [isBachFlowers, setIsBachFlowers] = useState(false);
  
  // Vitality Report specific data (for "Naturopathie Adultes - Bilan de vitalité")
  const [vitalityReportData, setVitalityReportData] = useState<VitalityReportData>(createDefaultVitalityReport());
  const [isVitalityReport, setIsVitalityReport] = useState(false);
  
  const [report, setReport] = useState<SessionReportType>({
    patient_id: "",
    child_id: null,
    accompaniment_id: null,
    accompaniment_title: "",
    report_date: new Date().toISOString().split("T")[0],
    hygiene_vie: [],
    conseils_alimentaires: [],
    soutiens_naturels: [],
    routine_bien_etre: [],
    notes: null,
    bach_flowers_data: null,
  });

  // Local draft persistence — survives network errors / browser crashes
  const draftKey = (report as any).id
    ? `session-report:${(report as any).id}`
    : selectedPatientId
    ? `session-report:new:${selectedPatientId}${selectedChildId ? `:${selectedChildId}` : ""}`
    : null;
  const draftState = useMemo(
    () => ({ report, bachFlowersData, vitalityReportData, isBachFlowers, isVitalityReport, selectedChildId, selectedAccompanimentId }),
    [report, bachFlowersData, vitalityReportData, isBachFlowers, isVitalityReport, selectedChildId, selectedAccompanimentId],
  );
  const { storedDraft, clearDraft } = useDraftPersistence(draftKey, draftState, { delayMs: 2000 });
  const draftPromptedRef = useRef(false);

  useEffect(() => {
    if (!storedDraft || draftPromptedRef.current) return;
    if (loading) return;
    draftPromptedRef.current = true;
    const ageMs = Date.now() - new Date(storedDraft.savedAt).getTime();
    if (ageMs > 7 * 24 * 3600 * 1000) {
      clearDraft();
      return;
    }
    if (JSON.stringify(storedDraft.data) === JSON.stringify(draftState)) return;
    const d: any = storedDraft.data;
    toast.info(`Brouillon restauré (${new Date(storedDraft.savedAt).toLocaleString("fr-FR")})`);
    if (d.report) setReport(d.report);
    if (d.bachFlowersData) setBachFlowersData(d.bachFlowersData);
    if (d.vitalityReportData) setVitalityReportData(d.vitalityReportData);
    if (typeof d.isBachFlowers === "boolean") setIsBachFlowers(d.isBachFlowers);
    if (typeof d.isVitalityReport === "boolean") setIsVitalityReport(d.isVitalityReport);
    if (d.selectedChildId !== undefined) setSelectedChildId(d.selectedChildId);
    if (d.selectedAccompanimentId !== undefined) setSelectedAccompanimentId(d.selectedAccompanimentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedDraft, loading]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (patientIdFromUrl && patients.length > 0) {
      setSelectedPatientId(patientIdFromUrl);
      const patient = patients.find(p => p.id === patientIdFromUrl);
      if (patient) {
        setSelectedPatient(patient);
        setReport(prev => ({ ...prev, patient_id: patientIdFromUrl }));
        loadPatientReports(patientIdFromUrl);
        loadPatientConsultations(patientIdFromUrl);
        loadPatientChildren(patientIdFromUrl);
      }
    }
  }, [patientIdFromUrl, patients]);

  const loadInitialData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      // Load patients
      const { data: patientsData, error: patientsError } = await supabase
        .from("patients")
        .select("id, name, email, date_of_birth, gender")
        .order("name");

      if (patientsError) throw patientsError;
      setPatients(patientsData || []);

      // Load accompaniments (both active and inactive for the practitioner)
      const { data: accompData, error: accompError } = await supabase
        .from("accompaniments")
        .select("id, title, is_active")
        .eq("user_id", user.id)
        .order("display_order");

      if (accompError) throw accompError;
      setAccompaniments(accompData || []);

    } catch (error: any) {
      toast.error("Erreur lors du chargement des données");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadPatientReports = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from("session_reports")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setExistingReports(data || []);
    } catch (error) {
      console.error("Error loading reports:", error);
    }
  };

  const loadPatientConsultations = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from("consultations")
        .select("id, patient_id, symptoms_text, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPatientConsultations(data || []);
    } catch (error) {
      console.error("Error loading consultations:", error);
    }
  };

  const loadConsultationRecommendations = async (consultationId: string) => {
    try {
      const { data, error } = await supabase
        .from("recommendations")
        .select("*")
        .eq("consultation_id", consultationId)
        .order("priority");

      if (error) throw error;
      
      const recommendationsWithSelection = (data || []).map(rec => ({
        ...rec,
        isSelected: false
      }));
      setConsultationRecommendations(recommendationsWithSelection);
    } catch (error) {
      console.error("Error loading recommendations:", error);
    }
  };

  const loadPatientChildren = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from("patient_children")
        .select("id, name, date_of_birth, gender, parent_id")
        .eq("parent_id", patientId)
        .order("name");

      if (error) throw error;
      setPatientChildren(data || []);
    } catch (error) {
      console.error("Error loading children:", error);
    }
  };

  // Reset complet du formulaire
  const resetForm = () => {
    setSelectedAccompanimentId("");
    setIsBachFlowers(false);
    setIsVitalityReport(false);
    setBachFlowersData(createDefaultBachFlowersSession());
    setVitalityReportData(createDefaultVitalityReport());
    setSelectedConsultationId("");
    setConsultationRecommendations([]);
    setImportedRecommendations([]);
    setSignatureDataUrl("");
    setReport({
      patient_id: selectedPatientId,
      child_id: null,
      accompaniment_id: null,
      accompaniment_title: "",
      report_date: new Date().toISOString().split("T")[0],
      hygiene_vie: [],
      conseils_alimentaires: [],
      soutiens_naturels: [],
      routine_bien_etre: [],
      notes: null,
      bach_flowers_data: null,
    });
  };

  const handlePatientChange = (patientId: string) => {
    setSelectedPatientId(patientId);
    const patient = patients.find(p => p.id === patientId);
    setSelectedPatient(patient || null);
    setSelectedChildId("");
    setSelectedChild(null);
    
    // Reset complet du formulaire quand on change de patient
    setSelectedAccompanimentId("");
    setIsBachFlowers(false);
    setIsVitalityReport(false);
    setBachFlowersData(createDefaultBachFlowersSession());
    setVitalityReportData(createDefaultVitalityReport());
    setSelectedConsultationId("");
    setConsultationRecommendations([]);
    setImportedRecommendations([]);
    setSignatureDataUrl("");
    setReport({
      patient_id: patientId,
      child_id: null,
      accompaniment_id: null,
      accompaniment_title: "",
      report_date: new Date().toISOString().split("T")[0],
      hygiene_vie: [],
      conseils_alimentaires: [],
      soutiens_naturels: [],
      routine_bien_etre: [],
      notes: null,
      bach_flowers_data: null,
    });
    
    loadPatientReports(patientId);
    loadPatientConsultations(patientId);
    loadPatientChildren(patientId);
  };

  const handleChildChange = (childId: string) => {
    setSelectedChildId(childId);
    if (childId) {
      const child = patientChildren.find(c => c.id === childId);
      setSelectedChild(child || null);
      setReport(prev => ({ ...prev, child_id: childId }));
      // Update bach flowers data if applicable
      if (isBachFlowers && child) {
        setBachFlowersData(prev => ({ ...prev, consultant_type: 'child' }));
      }
    } else {
      setSelectedChild(null);
      setReport(prev => ({ ...prev, child_id: null }));
      if (isBachFlowers) {
        setBachFlowersData(prev => ({ ...prev, consultant_type: 'adult' }));
      }
    }
  };

  // Helper to check if accompaniment is "Naturopathie Adultes - Bilan de vitalité"
  const isVitalityBilanAccompaniment = (title: string): boolean => {
    const normalizedTitle = title.toLowerCase().trim();
    return normalizedTitle.includes("bilan de vitalité") || 
           normalizedTitle.includes("bilan de vitalite") ||
           (normalizedTitle.includes("naturopathie") && normalizedTitle.includes("adulte") && normalizedTitle.includes("bilan"));
  };

  const handleAccompanimentChange = (accompanimentId: string) => {
    setSelectedAccompanimentId(accompanimentId);
    const accompaniment = accompaniments.find(a => a.id === accompanimentId);
    
    if (accompaniment) {
      const isBach = isBachFlowersAccompaniment(accompaniment.title);
      const isVitality = isVitalityBilanAccompaniment(accompaniment.title);
      
      setIsBachFlowers(isBach);
      setIsVitalityReport(isVitality);
      
      if (isBach) {
        setBachFlowersData(createDefaultBachFlowersSession());
      }
      
      if (isVitality) {
        setVitalityReportData(createDefaultVitalityReport());
      }
      
      const defaultContent = generateDefaultReport(accompaniment.title);
      setReport(prev => ({
        ...prev,
        accompaniment_id: accompanimentId,
        accompaniment_title: accompaniment.title,
        ...defaultContent
      }));
    }
  };

  const handleConsultationChange = (consultationId: string) => {
    setSelectedConsultationId(consultationId);
    if (consultationId) {
      loadConsultationRecommendations(consultationId);
    } else {
      setConsultationRecommendations([]);
    }
  };

  const toggleRecommendationSelection = (recId: string) => {
    setConsultationRecommendations(prev => 
      prev.map(rec => 
        rec.id === recId ? { ...rec, isSelected: !rec.isSelected } : rec
      )
    );
  };

  const importSelectedRecommendations = () => {
    const selected = consultationRecommendations.filter(rec => rec.isSelected);
    if (selected.length === 0) {
      toast.error("Sélectionnez au moins une recommandation");
      return;
    }

    const newItems: SessionReportItem[] = selected.map(rec => ({
      id: `imported-${rec.id}-${Date.now()}`,
      content: `**${rec.title}**\n${rec.detail}${rec.posology ? `\n_Posologie: ${rec.posology}_` : ""}${rec.contraindications && rec.contraindications.length > 0 ? `\n⚠️ Contre-indications: ${rec.contraindications.join(", ")}` : ""}`,
      isEditable: true,
      source: "analysis",
      originalType: rec.type,
    }));

    setImportedRecommendations(prev => [...prev, ...newItems]);
    
    // Deselect all
    setConsultationRecommendations(prev => 
      prev.map(rec => ({ ...rec, isSelected: false }))
    );
    
    toast.success(`${selected.length} recommandation(s) importée(s)`);
  };

  const removeImportedRecommendation = (itemId: string) => {
    setImportedRecommendations(prev => prev.filter(item => item.id !== itemId));
  };

  const updateImportedRecommendation = (itemId: string, content: string) => {
    setImportedRecommendations(prev => 
      prev.map(item => item.id === itemId ? { ...item, content } : item)
    );
  };

  const addItem = (section: keyof Pick<SessionReportType, 'hygiene_vie' | 'conseils_alimentaires' | 'soutiens_naturels' | 'routine_bien_etre'>) => {
    const newItem: SessionReportItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: "",
      isEditable: true,
    };
    setReport(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
  };

  const updateItem = (
    section: keyof Pick<SessionReportType, 'hygiene_vie' | 'conseils_alimentaires' | 'soutiens_naturels' | 'routine_bien_etre'>,
    itemId: string,
    content: string
  ) => {
    setReport(prev => ({
      ...prev,
      [section]: prev[section].map(item =>
        item.id === itemId ? { ...item, content } : item
      )
    }));
  };

  const removeItem = (
    section: keyof Pick<SessionReportType, 'hygiene_vie' | 'conseils_alimentaires' | 'soutiens_naturels' | 'routine_bien_etre'>,
    itemId: string
  ) => {
    setReport(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.id !== itemId)
    }));
  };

  const saveReport = async () => {
    if (!selectedPatientId || !selectedAccompanimentId) {
      toast.error("Veuillez sélectionner un consultant et un accompagnement");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Merge imported recommendations into soutiens_naturels
      const allSoutiensNaturels = [
        ...report.soutiens_naturels,
        ...importedRecommendations
      ];

      const reportData = {
        patient_id: selectedPatientId,
        child_id: selectedChildId || null,
        user_id: user.id,
        accompaniment_id: selectedAccompanimentId,
        accompaniment_title: report.accompaniment_title,
        report_date: report.report_date,
        hygiene_vie: isVitalityReport ? [] : JSON.parse(JSON.stringify(report.hygiene_vie)),
        conseils_alimentaires: isVitalityReport ? [] : JSON.parse(JSON.stringify(report.conseils_alimentaires)),
        soutiens_naturels: isVitalityReport ? [] : JSON.parse(JSON.stringify(allSoutiensNaturels)),
        routine_bien_etre: isVitalityReport ? [] : JSON.parse(JSON.stringify(report.routine_bien_etre)),
        notes: report.notes,
        bach_flowers_data: isBachFlowers ? JSON.parse(JSON.stringify(bachFlowersData)) : null,
        vitality_report_data: isVitalityReport ? JSON.parse(JSON.stringify(vitalityReportData)) : null,
      };

      const { data, error } = await supabase
        .from("session_reports")
        .insert([reportData as any])
        .select()
        .single();

      if (error) throw error;

      toast.success("Compte rendu enregistré avec succès");
      setReport(prev => ({ ...prev, id: data.id }));
      setImportedRecommendations([]);
      clearDraft();
      loadPatientReports(selectedPatientId);
      
    } catch (error: any) {
      toast.error("Erreur lors de l'enregistrement");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSignatureUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureDataUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateChildAge = (dateOfBirth: string | null): string | undefined => {
    if (!dateOfBirth) return undefined;
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return `${age} ans`;
  };

  const handleExportPDF = () => {
    if (!selectedPatient) {
      toast.error("Veuillez sélectionner un consultant");
      return;
    }
    
    // Prepare child info if applicable
    const childInfo = selectedChild ? {
      name: selectedChild.name,
      age: calculateChildAge(selectedChild.date_of_birth),
      gender: selectedChild.gender,
    } : undefined;
    
    // Use specific PDF generator for Vitality Report
    if (isVitalityReport) {
      downloadVitalityReportPDF({
        data: vitalityReportData,
        patientName: selectedPatient.name,
        patientDateOfBirth: selectedPatient.date_of_birth,
        patientGender: selectedPatient.gender,
        accompanimentTitle: report.accompaniment_title,
        reportDate: report.report_date,
        signatureDataUrl,
        notes: report.notes || undefined,
        childInfo,
      });
      toast.success("PDF exporté avec succès");
      return;
    }
    
    // Include imported recommendations in soutiens_naturels for standard PDF
    const reportWithImported = {
      ...report,
      soutiens_naturels: [...report.soutiens_naturels, ...importedRecommendations],
      bach_flowers_data: isBachFlowers ? bachFlowersData : null,
    };
    
    downloadSessionReportPDF(reportWithImported, selectedPatient.name, signatureDataUrl, childInfo);
    toast.success("PDF exporté avec succès");
  };

  const handleSendEmail = async () => {
    if (!selectedPatient) {
      toast.error("Veuillez sélectionner un consultant");
      return;
    }
    
    if (!selectedPatient.email) {
      toast.error("Ce consultant n'a pas d'adresse email enregistrée");
      return;
    }

    setSendingEmail(true);
    try {
      // Prepare child info if applicable
      const childInfo = selectedChild ? {
        name: selectedChild.name,
        age: calculateChildAge(selectedChild.date_of_birth),
        gender: selectedChild.gender,
      } : undefined;
      
      let pdfBuffer: Uint8Array;
      
      // Use specific PDF generator for Vitality Report
      if (isVitalityReport) {
        pdfBuffer = generateVitalityReportPDF({
          data: vitalityReportData,
          patientName: selectedPatient.name,
          patientDateOfBirth: selectedPatient.date_of_birth,
          patientGender: selectedPatient.gender,
          accompanimentTitle: report.accompaniment_title,
          reportDate: report.report_date,
          signatureDataUrl,
          notes: report.notes || undefined,
          childInfo,
        });
      } else {
        // Include imported recommendations in soutiens_naturels for standard email
        const reportWithImported = {
          ...report,
          soutiens_naturels: [...report.soutiens_naturels, ...importedRecommendations],
          bach_flowers_data: isBachFlowers ? bachFlowersData : null,
        };
        
        pdfBuffer = generateSessionReportPDF(reportWithImported, selectedPatient.name, signatureDataUrl, childInfo);
      }
      
      const { data, error } = await supabase.functions.invoke("send-session-report", {
        body: {
          email: selectedPatient.email,
          patientName: selectedPatient.name,
          childName: selectedChild?.name,
          accompanimentTitle: report.accompaniment_title,
          reportDate: report.report_date,
          pdfBuffer: Array.from(pdfBuffer),
        },
      });

      if (error) throw error;

      toast.success(`Email envoyé à ${selectedPatient.email}`);
    } catch (error: any) {
      toast.error("Erreur lors de l'envoi de l'email");
      console.error(error);
    } finally {
      setSendingEmail(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'nutrition': return <Utensils className="w-4 h-4" />;
      case 'phytotherapie': return <Leaf className="w-4 h-4" />;
      case 'hygiene_vie': return <Heart className="w-4 h-4" />;
      case 'complement': return <Pill className="w-4 h-4" />;
      case 'technique_bien_etre': return <Activity className="w-4 h-4" />;
      default: return <Leaf className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'nutrition': 'Nutrition',
      'phytotherapie': 'Phytothérapie',
      'hygiene_vie': 'Hygiène de vie',
      'complement': 'Complément',
      'technique_bien_etre': 'Bien-être'
    };
    return labels[type] || type;
  };

  const renderSection = (
    title: string,
    icon: React.ReactNode,
    section: keyof Pick<SessionReportType, 'hygiene_vie' | 'conseils_alimentaires' | 'soutiens_naturels' | 'routine_bien_etre'>,
    items: SessionReportItem[]
  ) => (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            {icon}
            {title}
          </CardTitle>
          <Button onClick={() => addItem(section)} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-4">
            Aucun élément. Cliquez sur "Ajouter" pour commencer.
          </p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg group">
              <GripVertical className="w-4 h-4 mt-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
              <Textarea
                value={item.content}
                onChange={(e) => updateItem(section, item.id, e.target.value)}
                placeholder="Entrez votre recommandation..."
                className="flex-1 min-h-[60px] resize-none"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeItem(section, item.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-primary">Chargement...</div>
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Compte Rendu de Séance"
        subtitle="Créez et gérez vos comptes rendus naturopathiques"
        icon={<ClipboardList className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="primary"
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleExportPDF}
              disabled={!selectedPatientId || !selectedAccompanimentId}
              className="gap-2"
            >
              <FileDown className="w-4 h-4" />
              Exporter PDF
            </Button>
            <Button
              variant="outline"
              onClick={handleSendEmail}
              disabled={!selectedPatientId || !selectedAccompanimentId || sendingEmail || !selectedPatient?.email}
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              {sendingEmail ? "Envoi..." : "Envoyer par email"}
            </Button>
            <Button
              onClick={saveReport}
              disabled={saving || !selectedPatientId || !selectedAccompanimentId}
              className="gap-2 bg-gradient-primary"
            >
              <Save className="w-4 h-4" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
          {/* Header Card */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">

                <FileText className="w-5 h-5" />
                Compte Rendu de Séance
              </CardTitle>
              <CardDescription>
                Créez un compte rendu personnalisé pour votre consultant
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Consultant *</Label>
                  <Select value={selectedPatientId} onValueChange={handlePatientChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un consultant" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id}>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            {patient.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Child selector - only show if patient has children */}
                {patientChildren.length > 0 && (
                  <div className="space-y-2">
                    <Label>Enfant (optionnel)</Label>
                    <Select value={selectedChildId || "none"} onValueChange={(v) => handleChildChange(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un enfant" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Adulte (consultant)
                          </div>
                        </SelectItem>
                        {patientChildren.map((child) => (
                          <SelectItem key={child.id} value={child.id}>
                            <div className="flex items-center gap-2">
                              <Baby className="w-4 h-4" />
                              {child.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Accompagnement *</Label>
                  <Select value={selectedAccompanimentId} onValueChange={handleAccompanimentChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un accompagnement" />
                    </SelectTrigger>
                    <SelectContent>
                      {accompaniments.map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          <div className="flex items-center gap-2">
                            <Heart className="w-4 h-4" />
                            {acc.title}
                            {!acc.is_active && (
                              <Badge variant="outline" className="ml-2 text-xs">Inactif</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Analyse liée (optionnel)</Label>
                  <Select 
                    value={selectedConsultationId || "none"} 
                    onValueChange={(value) => handleConsultationChange(value === "none" ? "" : value)}
                    disabled={!selectedPatientId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={selectedPatientId ? "Sélectionner une analyse" : "Sélectionnez d'abord un consultant"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune analyse</SelectItem>
                      {patientConsultations.map((consultation) => (
                        <SelectItem key={consultation.id} value={consultation.id}>
                          <div className="flex items-center gap-2">
                            <FlaskConical className="w-4 h-4" />
                            <span className="truncate max-w-[180px]">{consultation.symptoms_text}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(consultation.created_at).toLocaleDateString("fr-FR")}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date de la séance</Label>
                  <Input
                    type="date"
                    value={report.report_date}
                    onChange={(e) => setReport(prev => ({ ...prev, report_date: e.target.value }))}
                  />
                </div>
              </div>

              {selectedPatient && (
                <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                  <Badge variant="secondary">
                    <Users className="w-3 h-3 mr-1" />
                    {selectedPatient.name}
                  </Badge>
                  {selectedPatient.email && (
                    <Badge variant="outline">
                      <Mail className="w-3 h-3 mr-1" />
                      {selectedPatient.email}
                    </Badge>
                  )}
                  {selectedConsultationId && (
                    <Badge variant="outline" className="bg-primary/10">
                      <FlaskConical className="w-3 h-3 mr-1" />
                      Analyse liée
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recommendations from Analysis */}
          {selectedConsultationId && consultationRecommendations.length > 0 && (
            <Card className="shadow-card border-primary/20">
              <Collapsible open={isRecommendationsOpen} onOpenChange={setIsRecommendationsOpen}>
                <CardHeader className="pb-3">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between cursor-pointer">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <FlaskConical className="w-5 h-5 text-primary" />
                        Recommandations de l'analyse
                        <Badge variant="secondary" className="ml-2">
                          {consultationRecommendations.length}
                        </Badge>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            importSelectedRecommendations();
                          }}
                          disabled={!consultationRecommendations.some(r => r.isSelected)}
                          className="gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Importer ({consultationRecommendations.filter(r => r.isSelected).length})
                        </Button>
                        {isRecommendationsOpen ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CardDescription>
                    Sélectionnez les recommandations à ajouter au compte rendu
                  </CardDescription>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent>
                    <ScrollArea className="max-h-[400px]">
                      <div className="grid gap-3">
                        {consultationRecommendations.map((rec) => (
                          <div 
                            key={rec.id} 
                            className={`flex items-start gap-3 p-4 rounded-lg border transition-all cursor-pointer ${
                              rec.isSelected 
                                ? 'bg-primary/10 border-primary/30' 
                                : 'bg-muted/30 border-transparent hover:border-primary/20'
                            }`}
                            onClick={() => toggleRecommendationSelection(rec.id)}
                          >
                            <Checkbox 
                              checked={rec.isSelected}
                              onCheckedChange={() => toggleRecommendationSelection(rec.id)}
                              className="mt-1"
                            />
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="gap-1">
                                  {getTypeIcon(rec.type)}
                                  {getTypeLabel(rec.type)}
                                </Badge>
                                <span className="font-medium">{rec.title}</span>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {rec.detail}
                              </p>
                              {rec.posology && (
                                <p className="text-xs text-primary/80 italic">
                                  Posologie: {rec.posology}
                                </p>
                              )}
                              {rec.contraindications && rec.contraindications.length > 0 && (
                                <div className="flex items-center gap-1 text-xs text-amber-600">
                                  <AlertTriangle className="w-3 h-3" />
                                  {rec.contraindications.join(", ")}
                                </div>
                              )}
                            </div>
                            {rec.isSelected && (
                              <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}

          {/* Imported Recommendations Section */}
          {importedRecommendations.length > 0 && (
            <Card className="shadow-card border-green-500/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    Recommandations importées de l'analyse
                    <Badge variant="secondary" className="ml-2 bg-green-500/10 text-green-600">
                      {importedRecommendations.length}
                    </Badge>
                  </CardTitle>
                </div>
                <CardDescription>
                  Ces recommandations seront incluses dans la section "Soutiens naturels" du compte rendu
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {importedRecommendations.map((item) => (
                  <div key={item.id} className="flex items-start gap-2 p-3 bg-green-500/5 border border-green-500/20 rounded-lg group">
                    <GripVertical className="w-4 h-4 mt-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
                    <Textarea
                      value={item.content}
                      onChange={(e) => updateImportedRecommendation(item.id, e.target.value)}
                      className="flex-1 min-h-[80px] resize-none"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeImportedRecommendation(item.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Content Sections */}
          {selectedAccompanimentId && (
            <>
              <Separator className="my-6" />
              
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-foreground">Sections du compte rendu</h2>
                <p className="text-sm text-muted-foreground">Personnalisez chaque section selon les besoins du consultant</p>
              </div>

              {/* Bach Flowers specialized form */}
              {isBachFlowers && (
                <BachFlowersSessionForm
                  patientId={selectedPatientId}
                  childId={selectedChildId || null}
                  data={bachFlowersData}
                  onDataChange={setBachFlowersData}
                />
              )}

              {/* Vitality Report specialized form */}
              {isVitalityReport && (
                <VitalityReportForm
                  data={vitalityReportData}
                  onDataChange={setVitalityReportData}
                />
              )}

              {/* Standard sections (shown only for non-Bach Flowers and non-Vitality Report) */}
              {!isBachFlowers && !isVitalityReport && (
                <>
                  {renderSection(
                    "1. Ajustements d'hygiène de vie",
                    <Sparkles className="w-5 h-5 text-primary" />,
                    "hygiene_vie",
                    report.hygiene_vie
                  )}

                  {renderSection(
                    "2. Conseils alimentaires",
                    <Utensils className="w-5 h-5 text-primary" />,
                    "conseils_alimentaires",
                    report.conseils_alimentaires
                  )}

                  {renderSection(
                    "3. Soutiens naturels recommandés",
                    <Leaf className="w-5 h-5 text-primary" />,
                    "soutiens_naturels",
                    report.soutiens_naturels
                  )}

                  {renderSection(
                    "4. Routine bien-être personnalisée",
                    <Heart className="w-5 h-5 text-primary" />,
                    "routine_bien_etre",
                    report.routine_bien_etre
                  )}
                </>
              )}

              {/* Notes */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Notes complémentaires</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={report.notes || ""}
                    onChange={(e) => setReport(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Ajoutez des notes ou observations supplémentaires..."
                    rows={4}
                  />
                </CardContent>
              </Card>

              {/* Signature */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Signature électronique</CardTitle>
                  <CardDescription>
                    Ajoutez votre signature pour valider le compte rendu
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleSignatureUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {signatureDataUrl ? "Changer la signature" : "Importer une signature"}
                    </Button>
                    {signatureDataUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setSignatureDataUrl("");
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    )}
                  </div>
                  {signatureDataUrl && (
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <img
                        src={signatureDataUrl}
                        alt="Signature"
                        className="max-h-24 object-contain"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Existing Reports */}
          {existingReports.length > 0 && (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg">Comptes rendus précédents</CardTitle>
                <CardDescription>{existingReports.length} compte(s) rendu(s) pour ce consultant</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {existingReports.map((existingReport) => (
                    <div
                      key={existingReport.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{existingReport.accompaniment_title}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(existingReport.report_date).toLocaleDateString("fr-FR", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Parse the JSON fields properly
                          const parsedReport = {
                            ...existingReport,
                            hygiene_vie: existingReport.hygiene_vie || [],
                            conseils_alimentaires: existingReport.conseils_alimentaires || [],
                            soutiens_naturels: existingReport.soutiens_naturels || [],
                            routine_bien_etre: existingReport.routine_bien_etre || [],
                          };
                          setReport(parsedReport);
                          setSelectedAccompanimentId(existingReport.accompaniment_id || "");
                          
                          // Restore special form state if applicable
                          const accTitle = existingReport.accompaniment_title || "";
                          const isVitality = isVitalityBilanAccompaniment(accTitle);
                          const isBach = isBachFlowersAccompaniment(accTitle);
                          
                          setIsVitalityReport(isVitality);
                          setIsBachFlowers(isBach);
                          
                          if (isVitality && existingReport.vitality_report_data) {
                            setVitalityReportData(existingReport.vitality_report_data);
                          }
                          if (isBach && existingReport.bach_flowers_data) {
                            setBachFlowersData(existingReport.bach_flowers_data);
                          }
                        }}
                      >
                        Charger
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
        )}
      </div>
    </PageContainer>
  );
}
